"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
export default function FrequentlyAskedQuestions() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    numeral: "01.",
    question: "How does Chipper Mulching Co ensure site safety?",
    answer: "Safety is our primary operational standard. Our team utilizes professional-grade equipment with integrated safety features, and every operator is fully trained in site assessment and hazard mitigation. We maintain comprehensive insurance coverage and follow strict protocols to protect your property and our crew during every clearing project."
  }, {
    numeral: "02.",
    question: "What is the difference between mulching and traditional clearing?",
    answer: "Traditional clearing often involves hauling away debris, burning, or burying, which can be costly and environmentally taxing. Forestry mulching grinds vegetation directly into the soil, creating a nutrient-rich layer of mulch that prevents erosion, suppresses weeds, and improves soil health while eliminating the need for off-site disposal."
  }, {
    numeral: "03.",
    question: "Can you clear land during wet weather conditions?",
    answer: "While we strive to accommodate your schedule, heavy rain can impact soil stability and equipment performance. We assess each site individually to ensure that our machinery does not cause unnecessary rutting or damage to your land. We will communicate clearly if a delay is necessary to maintain the integrity of your property."
  }, {
    numeral: "04.",
    question: "How do you determine the cost of a project?",
    answer: "Pricing is based on the size of the area, the density of the vegetation, and the terrain conditions. We provide free, on-site estimates to evaluate your specific needs and provide a transparent, fixed-price quote. This ensures you know exactly what to expect before we begin any work on your land."
  }, {
    numeral: "05.",
    question: "Do I need to be present during the clearing process?",
    answer: "You do not need to be present once we have established the scope of work and marked the boundaries. We pride ourselves on clear communication and will provide updates throughout the process. We ensure that all work is completed to your exact specifications, leaving your property clean and ready for your next steps."
  }, {
    numeral: "06.",
    question: "What types of vegetation can your equipment handle?",
    answer: "Our heavy-duty mulchers are designed to handle everything from thick brush and invasive species to small trees and dense undergrowth. Whether you are reclaiming pasture, creating firebreaks, or preparing a site for construction, our equipment efficiently processes woody material into a fine, manageable mulch."
  }];
  return <section data-section-id="3573" id="frequently-asked-questions" className="bg-background py-32 font-default text-foreground">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div ref={headerRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="text-xs uppercase tracking-widest text-accent-foreground font-sans mb-8">
                Helpful Information
              </p>
              <h2 className="font-default text-foreground text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                <span className="block text-secondary">Common</span>
                <span className="block text-secondary">Questions,</span>
                <span className="block italic font-light text-primary">
                  Answered.
                </span>
              </h2>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div ref={listRef} className={`transition-all duration-700 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-b border-secondary last:border-b-0 group">
                    <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden">
                      <div className="flex items-start gap-6 flex-1 text-left">
                        <span className="text-sm font-sans text-accent-foreground tracking-wider pt-2 shrink-0">
                          {faq.numeral}
                        </span>
                        <span className="font-default text-foreground text-2xl leading-snug tracking-tight">
                          {faq.question}
                        </span>
                      </div>
                      <span aria-hidden="true" className="font-default text-3xl font-light text-foreground/70 ml-6 shrink-0 leading-none transition-transform duration-400 ease-out group-data-[state=open]:rotate-45">
                        +
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8">
                      <div className="pl-0 md:pl-14 pr-0 md:pr-12 py-2">
                        <p className="font-default text-muted-foreground text-[1.0625rem] leading-[1.7]">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>

              {}
              <div className="mt-16 pt-8 border-t border-secondary">
                <p className="font-default italic text-lg text-muted-foreground">
                  Still have questions?{" "}
                  <Link to="/ContactUsPage" className="text-foreground not-italic font-default relative inline-block group">
                    <span className="inline-block">
                      Contact our team
                      <span className="ml-1 inline-block transition-transform duration-300 group-hover:translate-x-1">
                        &rarr;
                      </span>
                    </span>
                    <span className="absolute left-0 -bottom-0.5 h-px w-full bg-foreground origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-500 ease-out" />
                    <span className="absolute left-0 -bottom-0.5 h-px w-full bg-accent-foreground origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out delay-100" />
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}